import { useEffect, useState } from "react";
import { api, money, day } from "../api.js";
import { useResource } from "./useResource.js";
import { shortName } from "./common.jsx";

// «Комплаєнс»: позитивна податкова історія (як в etax) і оцінка для «Території високого рівня податкової довіри».
// Рекомендації — короткими пронумерованими кроками (docs/recommendations-format.md)
const uah = (v) => `${Math.round(Number(v) || 0).toLocaleString("uk-UA")} грн`;
const Icon = ({ s }) => <span className={`cmp-dot cmp-${s}`} aria-hidden="true">{s === "ok" ? "✓" : s === "fail" ? "✕" : "?"}</span>;
const STATE_TEXT = { ok: "виконується", fail: "не виконується", unknown: "невідомо" };

function Rec({ r }) {
  if (!r) return null;
  return (
    <div className="cmp-rec">
      <p><b>{r.title}.</b> {r.why}</p>
      {r.steps?.length > 0 && <ol>{r.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>}
      {r.until && <p className="muted small">{r.until}</p>}
    </div>
  );
}

// Дві вкладки з підсумком на кожній; вибрана — в адресі (#/c/<id>/compliance/trust), щоб посилання вело точно
export default function Compliance({ companyId, company, sub }) {
  const tab = sub === "trust" ? "trust" : "history";
  const ph = useResource(`/api/companies/${companyId}/compliance/positive-history`);
  const wc = useResource(`/api/companies/${companyId}/compliance/white-club`); // оцінка за останній період — і для підсумку, і для вкладки
  const wr = wc.data?.result;
  const strong = ph.data?.strong || [];
  const phTone = !ph.data ? "" : strong.length || !ph.data.data.deficit.length ? "ok" : "bad";
  const go = (t) => { location.hash = `#/c/${companyId}/compliance${t === "trust" ? "/trust" : ""}`; };
  const tabs = [
    { key: "history", label: "Позитивна історія", tone: phTone,
      head: !ph.data ? "…" : strong.length ? `Діє ознака ${strong.join(", ")}` : (ph.data.marks[0]?.cabinet.ok ?? ph.data.marks[0]?.ours) === true ? "Лише ознака 1" : "Ознак 2–9 немає",
      sub: ph.data?.limit.title || "" },
    { key: "trust", label: "Податкова довіра", tone: !wr || !wr.available ? "" : wr.ok ? "ok" : "bad",
      head: wc.error ? "Оцінку не отримано" : !wr ? "…" : !wr.available ? "Оцінка недоступна" : wr.ok ? "Вимоги виконано" : `Є перешкоди: ${wr.blockers.length}`,
      sub: wr?.available ? `Оцінка ДПС за ${wc.data.periods.find((x) => x.rdate === wc.data.rdate)?.name || wc.data.rdate}` : "Що заважає включенню до Переліку" },
  ];
  return (
    <>
      <div className="section-head"><div className="section-title"><h2>Комплаєнс</h2>
        <p className="status">Позитивна податкова історія і відповідність вимогам податкової довіри</p></div></div>
      <div className="cmp-summary" role="tablist" aria-label="Комплаєнс">
        {tabs.map((t) => (
          <button type="button" key={t.key} role="tab" id={`cmp-tab-${t.key}`} aria-selected={tab === t.key} aria-controls={`cmp-panel-${t.key}`}
            className={`cmp-card ${t.tone}${tab === t.key ? " on" : ""}`} onClick={() => go(t.key)}>
            <span className="muted small">{t.label}</span><b>{t.head}</b><span className="small">{t.sub}</span>
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`cmp-panel-${tab}`} aria-labelledby={`cmp-tab-${tab}`}>
        {tab === "history" ? <PositiveHistory companyId={companyId} company={company} /> : <WhiteClub companyId={companyId} initial={wc} />}
      </div>
    </>
  );
}

function PositiveHistory({ companyId, company }) {
  const [plan, setPlan] = useState("");
  const [reload, setReload] = useState(0);
  const ph = useResource(`/api/companies/${companyId}/compliance/positive-history${plan ? `?plan=${Number(plan.replace(/\s/g, "").replace(",", ".")) || ""}` : ""}`, reload);
  const canEdit = ["preparer", "submitter", "owner"].includes(company.role);
  if (ph.error) return <section className="card" id="cmp-ph"><p className="error">{ph.error.message}</p></section>;
  if (!ph.data) return <section className="card" id="cmp-ph"><p className="muted">Рахуємо позитивну історію…</p></section>;
  const x = ph.data, dd = x.data;
  if (!dd.hasData) return <section className="card" id="cmp-ph"><h3>Позитивна податкова історія</h3><p className="muted">Немає виданих ПН — синхронізуйте розділ ПН/РК.</p></section>;
  return (
    <section className="card cmp-section" id="cmp-ph" aria-labelledby="cmp-ph-h">
      <h3 id="cmp-ph-h">Позитивна податкова історія</h3>
      <p className="muted small">Додаток 2 до Порядку зупинення реєстрації ПН (ПКМУ № 1165). {dd.ppiDate ? `Позначки ознак — висновок кабінету на ${day(dd.ppiDate)}.` : "Висновку кабінету ще немає — оновіть СМКОР."}</p>

      <h4>Як зняти ліміт 500 000</h4>
      <Rec r={x.limit} />

      <h4>Ознаки позитивної історії</h4>
      <ul className="cmp-marks">{x.marks.map((m) => <Mark key={m.n} m={m} />)}</ul>

      <details className="cmp-more"><summary>Скільки ознак потрібно</summary><Rec r={x.howMany} /></details>
      <details className="cmp-more"><summary>Показники моніторингу: D і P</summary>
        <Rec r={x.dExplain} /><Rec r={x.pExplain} />
        <p className="muted small">{dd.cabD != null
          ? `З кабінету${dd.cabDate ? ` на ${day(dd.cabDate)}` : ""}: S = ${uah(dd.cabS)}, T = ${uah(dd.cabT)}, Рнайб = ${uah(dd.cabPM)}, Рпоточ за 3 місяці: ${dd.cabPP.map(uah).join(" / ")}. Рпоточ за ${dd.month} за нашими даними: ${uah(dd.pCur)}.`
          : "Даних моніторингу з кабінету ще немає — оновіть СМКОР."}</p>
      </details>
      <details className="cmp-more"><summary>Калькулятор: що дасть кожен шлях</summary>
        <label className="field field-inline cmp-plan">Скільки хочете реєструвати на місяць, без ПДВ
          <input inputMode="decimal" placeholder={String(x.calc.plan)} value={plan} onChange={(e) => setPlan(e.target.value)} /></label>
        <p className="muted small">Зараз: D {x.d != null ? `${(x.d * 100).toFixed(2).replace(".", ",")} %` : "—"}, сплачено за 12 міс. {uah(x.sYear)}, продажі за 12 міс. {uah(x.tYear)}.</p>
        <ul className="cmp-paths">
          {x.calc.paths.map((p) => (
            <li key={p.id}>
              <p><Icon s={p.ok === true ? "ok" : p.ok === false ? "fail" : "unknown"} /> <b>{p.title}</b></p>
              <div className="cmp-two"><div><p className="muted small">Витрати</p><ol>{p.costs.map((c, i) => <li key={i}>{c}</li>)}</ol></div>
                <div><p className="muted small">Що дасть</p><ol>{p.gives.map((c, i) => <li key={i}>{c}</li>)}</ol></div></div>
            </li>
          ))}
        </ul>
        <p className="muted small">Розрахунок за ставкою ПДВ 20 %; ПДФО 18 %, військовий збір 5 %, ЄСВ 22 %, мінімальна зарплата 8 647 грн.</p>
      </details>
      {dd.stopFromCabinet && (
        <details className="cmp-more"><summary>Зупинені ПН (з кабінету): досі зупинено {dd.stoppedOpen.length}</summary>
          <p className="small">Усього зупинялось {dd.stopTotal}, зареєстровано комісією {dd.stopRegistered}, досі зупинено за 180 днів {dd.stoppedOpen.length}.</p>
          <ul className="cmp-list">{dd.stoppedOpen.map((s, i) => (
            <li key={i} className={s.expl ? "" : "bad"}>№ {s.nmr} від {day(s.date)} — {shortName(s.buyer)}: {s.expl ? `${s.expl}, чекає рішення комісії` : "пояснень не подано"}</li>
          ))}</ul>
          <p><a href={`#/c/${companyId}/smkor`}>До СМКОР →</a></p>
        </details>
      )}
      <details className="cmp-more"><summary>Ліміти покупців цього місяця</summary>
        {!dd.buyers.length ? <p className="muted small">Цього місяця ще нічого не зареєстровано.</p> : (
          <ul className="cmp-list">{dd.buyers.map((b) => <li key={b.tin}>{shortName(b.name)}: зареєстровано {uah(b.registered)}, лишається <b className={b.left <= 0 ? "error" : ""}>{uah(b.left)}</b></li>)}</ul>
        )}
      </details>
      <Manual companyId={companyId} manual={x.manual} cabD={dd.cabD} canEdit={canEdit} onSaved={() => setReload((v) => v + 1)} />
    </section>
  );
}

// Одна ознака: перший рівень — висновок ДПС, наш розрахунок, чого бракує і що зробити; варіанти; «Показати розрахунок»
const yesNo = (v) => (v === true ? "так" : v === false ? "ні" : "невідомо");
const tone = (v) => (v === true ? "ok" : v === false ? "fail" : "unknown");
function Mark({ m }) {
  const main = m.cabinet.ok ?? m.ours;
  const missing = m.conds.filter((c) => c.ok !== true);
  return (
    <li className={`cmp-mark ${tone(main)}`}>
      <details>
        <summary>
          <Icon s={tone(main)} />
          <span className="cmp-mark-h"><b>{m.n}. {m.title}</b> <span className="sr-only">— {STATE_TEXT[tone(main)]}</span>
            <span className="cmp-mark-st small">
              <span className={`tag ${tone(m.cabinet.ok)}`}>ДПС: {yesNo(m.cabinet.ok)}{m.cabinet.date ? ` на ${day(m.cabinet.date)}` : ""}</span>
              <span className={`tag ${tone(m.ours)}`}>наш розрахунок: {yesNo(m.ours)}</span>
            </span>
          </span>
        </summary>
        <div className="cmp-mark-body">
          <p className="small"><b>Що дає:</b> {m.gives}</p>
          {missing.length > 0 && <>
            <p className="small"><b>{m.ours === false ? "Чого бракує" : "Що не перевірено"}:</b></p>
            <ul className="cmp-conds small">{m.conds.map((c, i) => (
              <li key={i} className={tone(c.ok)}><Icon s={tone(c.ok)} /> <span>{c.label}{c.gap ? <> — <b>{c.gap}</b></> : null}</span></li>
            ))}</ul>
          </>}
          {missing.length === 0 && <p className="small">Усі умови виконано за нашими даними.</p>}
          {m.todo.length > 0 && <><p className="small"><b>Що зробити:</b></p><ol className="small">{m.todo.map((t, i) => <li key={i}>{t}</li>)}</ol></>}
          {m.options?.length > 0 && <>
            <p className="small"><b>Варіанти:</b></p>
            <ul className="cmp-options small">{m.options.map((o) => (
              <li key={o.title}><p><b>{o.title}</b>{o.model && <span className="tag unknown">модельний</span>}</p>
                <p>{o.cost}</p><p>{o.eta}</p>
                {o.assumptions?.length > 0 && <p className="muted">Припущення: {o.assumptions.join(" ")}</p>}</li>
            ))}</ul>
          </>}
          <details className="cmp-calc small"><summary>Показати розрахунок</summary>
            {m.calc.table && (
              <div className="cmp-table-wrap"><table className="cmp-table">
                <thead><tr>{m.calc.table.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                <tbody>{m.calc.table.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
              </table></div>
            )}
            {m.calc.rows?.length > 0 && <dl className="cmp-dl">{m.calc.rows.map(([k, v], i) => <div key={i}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>}
            {m.calc.notes?.length > 0 && <ul className="muted">{m.calc.notes.map((t, i) => <li key={i}>{t}</li>)}</ul>}
            {m.calc.sources?.length > 0 && <p className="muted">Джерела: {m.calc.sources.join("; ")}.</p>}
          </details>
        </div>
      </details>
    </li>
  );
}

// Дані, яких немає в кабінеті — ручні відмітки (спільні для користувачів компанії)
const manualForm = (mn) => ({ directorConfirmed: mn.directorConfirmed === true, leaderSince: mn.leaderSince || "", founderSince: mn.founderSince || "",
  fixedAssets: !!mn.fixedAssets, landArea: mn.landArea || "", landKind: mn.landKind || "", landOk: !!mn.landOk, singleTax4: !!mn.singleTax4,
  pdfoOk: !!mn.pdfoOk, smallEnterprise: !!mn.smallEnterprise, dPct: mn.dPct || "" });
function Manual({ companyId, manual, cabD, canEdit, onSaved }) {
  const [m, setM] = useState(manualForm(manual));
  const [state, setState] = useState("");
  useEffect(() => {
    if (JSON.stringify(m) === JSON.stringify(manualForm(manual))) return;
    const t = setTimeout(async () => {
      setState("зберігаємо…");
      try { await api(`/api/companies/${companyId}/compliance/manual`, { method: "PUT", body: m }); setState("збережено"); onSaved(); } catch (e) { setState(e.message); }
    }, 700);
    return () => clearTimeout(t);
  }, [m]);
  const T = ({ k, children }) => <label className="check"><input type="checkbox" disabled={!canEdit} checked={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.checked })} />{children}</label>;
  const F = ({ k, label, ...rest }) => <label className="field field-inline">{label}<input disabled={!canEdit} value={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.value })} {...rest} /></label>;
  return (
    <details className="cmp-more"><summary>Дані, яких немає в кабінеті</summary>
      {cabD == null && F({ k: "dPct", label: "Показник D з останньої квитанції, %", placeholder: "напр. 2,44" })}
      <T k="directorConfirmed">Підтверджую: керівник очолює не більше 5 платників ПДВ (без відмітки — «невідомо»)</T>
      <p className="small muted">Ознака 3: дати, з яких не змінювались керівник і засновник (учасник) — за випискою з ЄДР.</p>
      {F({ k: "leaderSince", label: "Керівник не змінювався з", type: "date" })}
      {F({ k: "founderSince", label: "Засновник не змінювався з", type: "date" })}
      <T k="fixedAssets">Основні засоби понад 5 млн у балансі (лише якщо балансу немає в «Перегляді звітності»)</T>
      <p className="small muted">Ознака 4: земля на 1 січня поточного року.</p>
      {F({ k: "landArea", label: "Площа, га", inputMode: "decimal", placeholder: "напр. 250" })}
      <label className="field field-inline">Вид права
        <select disabled={!canEdit} value={m.landKind} onChange={(e) => setM({ ...m, landKind: e.target.value })}>
          <option value="">—</option><option value="own">власність чи користування (від 200 га)</option><option value="public_lease">оренда комунальної чи державної (від 0,5 га)</option>
        </select></label>
      <T k="landOk">Задекларовано до 20 лютого і плату за землю сплачено вчасно</T>
      <T k="singleTax4">Платник єдиного податку 4 групи, податок сплачено вчасно і повністю</T>
      <T k="pdfoOk">Ознака 5 виконується (лише якщо Податкових розрахунків немає в «Перегляді звітності»)</T>
      <T k="smallEnterprise">Мікро- чи мале підприємство (ч.2 ст.2 Закону «Про бухоблік») — для безумовної реєстрації агропродукції, п.3 пп.5</T>
      <p className="muted small">Висновок кабінету ці поля не змінюють — вони уточнюють наш розрахунок і підказки. {state}</p>
    </details>
  );
}

function WhiteClub({ companyId, initial }) {
  const [rdate, setRdate] = useState("");
  // Останній період уже прочитано для підсумку на вкладці — інший період читаємо окремо
  const other = useResource(rdate ? `/api/companies/${companyId}/compliance/white-club?rdate=${rdate}` : null);
  const wc = rdate ? other : initial;
  const r = wc.data?.result;
  return (
    <section className="card cmp-section" id="cmp-wc" aria-labelledby="cmp-wc-h">
      <div className="row">
        <h3 id="cmp-wc-h">Територія високого рівня податкової довіри</h3>
        {wc.data?.periods?.length > 0 && (
          <select className="erpn-month" aria-label="Період оцінки" value={wc.data.rdate} onChange={(e) => setRdate(e.target.value === initial.data?.rdate ? "" : e.target.value)}>
            {wc.data.periods.map((p) => <option key={p.rdate} value={p.rdate}>{p.name}</option>)}
          </select>
        )}
      </div>
      <p className="muted small">«Клуб білого бізнесу»: оцінка ДПС для включення до Переліку платників з високим рівнем добровільного дотримання податкового законодавства (пп. 69.41 ПКУ). Невідповідність сама по собі не означає ризиковість у СМКОР чи блокування ПН.</p>
      {wc.error ? <p className="error">{wc.error.message}</p> : wc.pending ? <p className="muted">Читаємо кабінет…</p> : !r.available ? (
        <p className="notice">{r.message} <a href="https://tpd.tax.gov.ua/" target="_blank" rel="noopener">tpd.tax.gov.ua</a></p>
      ) : (
        <>
          <div className={`cmp-verdict ${r.ok ? "ok" : "bad"}`}>
            <b>{r.ok ? "Вимоги виконано — компанія може потрапити до Переліку" : `Є перешкоди для включення: ${r.blockers.length}`}</b>
            {r.groupName && <span className="small"> · {r.groupName}</span>}
          </div>
          {!r.ok && (
            <>
              <h4>Що заважає включенню</h4>
              <ol className="cmp-blockers">{r.blockers.map((b) => (
                <li key={b.field}>
                  <p><b>{b.why || b.label}</b> <span className="tag">{b.value}</span></p>
                  {b.note && <p className="muted small">{b.note}</p>}
                  {b.todo && <p className="small">{b.todo}{b.section && <> <a href={`#/c/${companyId}/${b.section}`}>Перейти →</a></>}</p>}
                  <p className="muted small cmp-label">{b.label}</p>
                </li>
              ))}</ol>
            </>
          )}
          <details className="cmp-more"><summary>Усі вимоги (А–Ї) і критерії</summary>
            <ul className="cmp-reqs">{r.requirements.map((q) => (
              <li key={q.field}><Icon s={q.ok === true ? "ok" : q.ok === false ? "fail" : "unknown"} /><span>{q.label}</span><b className="cmp-val">{q.value}</b></li>
            ))}</ul>
            {r.criteria.length > 0 && (<>
              <h4>Критерії для вашої системи оподаткування</h4>
              <ul className="cmp-reqs">{r.criteria.map((q) => (
                <li key={q.field}><Icon s={q.ok === true ? "ok" : q.ok === false ? "fail" : "unknown"} /><span>{q.label}{q.note && <span className="muted small"> — {q.note}</span>}</span><b className="cmp-val">{q.value}</b></li>
              ))}</ul>
            </>)}
          </details>
          <details className="cmp-more"><summary>Що дає включення до Переліку</summary>
            <ol>{wc.data.benefits.map((b, i) => <li key={i}>{b}</li>)}</ol>
            <p className="small"><a href={wc.data.source} target="_blank" rel="noopener">Роз'яснення ДПС з урахуванням змін 2026 року</a></p>
          </details>
        </>
      )}
    </section>
  );
}
